import z from 'zod';
import type { Result } from './result.js';
import { SocialSecurityNumber } from './social_security_number.js';

/**
 *  Det går inte att helt säkert säga vilken företagsform som finns bakom ett organisationsnummer. För många företag och föreningar stämmer dock följande lista.
 *
 *  1  Dödsbon
 *  2  Stat, regioner, kommuner, församlingar
 *  3  Utländska företag som bedriver näringsverksamhet eller äger fastigheter i Sverige
 *  5  Aktiebolag
 *  7  Ekonomiska föreningar, bostadsrättsföreningar och samfällighetsföreningar
 *  8  Ideella föreningar och stiftelser
 *  9  Handelsbolag och kommanditbolag
 */
const format = /^(SE)?(16)?([1235789][0-9][2-9][0-9]{3})-?([0-9]{4})$/;

export const companyTypeClasses = [
  'LimitedCompany',
  'Association',
  'TradingCompanyLimitedPartnerShip',
  'SoleProprietorship',
  'Foundation',
  'ForeignCorporation',
  'Authority',
  'EstateOfDeceased',
  'Other',
] as const;
export type CompanyTypeClass = (typeof companyTypeClasses)[number];

export const companyTypes = [
  'LimitedCompany',
  'EconomicAssociation',
  'HousingCooperativeAssociation',
  'GeneralPartnership',
  'LimitedPartnership',
  'SoleProprietorship',
  'NonProfitAssociation',
  'ForeignBranchWithinEES',
  'ForeignBranchOutsideEES',
  'PensionOrPersonnelFoundation',
  'FoundationWithAnnualFinancialStatements',
  'Authority',
  'Other',
] as const;
export const companyTypeSchema = z.enum(companyTypes);
export type CompanyType = z.infer<typeof companyTypeSchema>;
export function isCompanyType(arg: unknown): arg is CompanyType {
  return typeof arg === 'string' && companyTypes.includes(arg as CompanyType);
}

export function translateCompanyTypeClass(type: CompanyTypeClass, sentence = false) {
  if (sentence) {
    switch (type) {
      case 'LimitedCompany':
        return 'ett aktiebolag';
      case 'Foundation':
        return 'en ideell förening eller stiftelse';
      case 'TradingCompanyLimitedPartnerShip':
        return 'ett handelsbolag eller kommanditbolag';
      case 'SoleProprietorship':
        return 'en enskild firma';
      case 'Association':
        return 'en ekonomiska förening, bostadsrättsförening eller samfällighetsförening';
      case 'ForeignCorporation':
        return 'ett utländskt företag som bedriver näringsverksamhet eller äger fastigheter i Sverige';
      case 'Authority':
        return 'en myndighet';
      case 'EstateOfDeceased':
        return 'ett dödsbo';
      case 'Other':
        return 'en okänd företagstyp';
      default:
        throw `Could not translate company type ${type} to a sentence`;
    }
  } else {
    switch (type) {
      case 'LimitedCompany':
        return 'Aktiebolag';
      case 'Foundation':
        return 'Ideell förening eller stiftelse';
      case 'TradingCompanyLimitedPartnerShip':
        return 'Handelsbolag eller kommanditbolag';
      case 'SoleProprietorship':
        return 'Enskild firma';
      case 'Association':
        return 'Ekonomiska förening, bostadsrättsförening eller samfällighetsförening';
      case 'ForeignCorporation':
        return 'Utländskt företag som bedriver näringsverksamhet eller äger fastigheter i Sverige';
      case 'Authority':
        return 'Myndighet';
      case 'EstateOfDeceased':
        return 'Dödsbo';
      case 'Other':
        return 'Annat';
      default:
        throw `Could not translate company type ${type}`;
    }
  }
}

export function translateCompanyType(type: CompanyType) {
  switch (type) {
    case 'LimitedCompany':
      return 'Aktiebolag';
    case 'EconomicAssociation':
      return 'Ekonomisk förening';
    case 'HousingCooperativeAssociation':
      return 'Bostadsrättsförening';
    case 'GeneralPartnership':
      return 'Handelsbolag';
    case 'LimitedPartnership':
      return 'Kommanditbolag';
    case 'SoleProprietorship':
      return 'Enskild näringsverksamhet';
    case 'NonProfitAssociation':
      return 'Ideell förening';
    case 'ForeignBranchWithinEES':
      return 'Utländsk filial EES';
    case 'ForeignBranchOutsideEES':
      return 'Utländsk filial utanför EES';
    case 'PensionOrPersonnelFoundation':
      return 'Pensions- eller personalstiftelse';
    case 'FoundationWithAnnualFinancialStatements':
      return 'Stiftelse med årsredovisning';
    case 'Authority':
      return 'Myndighet';
    case 'Other':
      return 'Annat';
    default:
      throw `Could not translate company type ${type}`;
  }
}

declare const corporateIdBrand: unique symbol;

/**
 * Swedish corporate id (organisationsnummer); the natural-person analogue is a
 * social security number. A `CorporateId` IS its canonical dashed string —
 * `XXXXXX-YYYY`, or `YYYYMMDD-XXXX` for a sole proprietorship — matching the Rust
 * `CorporateId` `SerializeToString`/`Display` wire form. Operations live on the
 * `CorporateId` namespace value below.
 */
export type CorporateId = string & { readonly [corporateIdBrand]: 'CorporateId' };

const brand = (value: string): CorporateId => value as CorporateId;

/** Canonical dashed rendering of a normalized digit string (dash before the last four). */
function toDashed(digits: string): CorporateId {
  const index = digits.length === 12 ? 8 : 6;
  return brand(digits.slice(0, index) + '-' + digits.slice(index));
}

/** Normalizes org-nr / SSN input to the canonical dashed form, or null when invalid. */
function normalizeCorporateId(input: string): CorporateId | null {
  try {
    return toDashed(SocialSecurityNumber.fromStringUnsafe(input).toString());
  } catch {
    const match = input.match(format);
    if (match === null) {
      return null;
    }
    return toDashed(match[3] + match[4]);
  }
}

export const CorporateId = {
  /** Parses org-nr / SSN input into the canonical dashed form. */
  fromString(input: string): Result<CorporateId> {
    return normalizeCorporateId(input) ?? new Error('Invalid string for corporate ID: ' + input);
  },

  schema: z.string().transform((input, ctx): CorporateId => {
    const normalized = normalizeCorporateId(input);
    if (normalized === null) {
      ctx.addIssue({ code: 'custom', message: `Invalid CorporateId: ${input}` });
      return z.NEVER;
    }
    return normalized;
  }),

  displayAcceptableFormat(): string {
    return 'XXXXXX-YYYY';
  },

  /** Best-guess company class from the leading digits. */
  companyTypeClassGuess(id: CorporateId): CompanyTypeClass {
    const digits = id.replace('-', '');
    if (digits.length === 12) {
      return 'SoleProprietorship';
    }
    switch (digits[0]) {
      case '1':
        return 'EstateOfDeceased';
      case '2':
        return 'Authority';
      case '3':
        return 'ForeignCorporation';
      case '5':
        return digits[1] === '1' ? 'ForeignCorporation' : 'LimitedCompany';
      case '7':
        return 'Association';
      case '8':
        return 'Foundation';
      case '9':
        return 'TradingCompanyLimitedPartnerShip';
      default:
        return 'Other';
    }
  },
};

/** Co-located schema; kept as a named export for the many existing importers. */
export const corporateIdSchema = CorporateId.schema;

export function companyTypeGuessFromClass(companyClass: CompanyTypeClass): CompanyType {
  switch (companyClass) {
    case 'LimitedCompany':
      return 'LimitedCompany';
    case 'SoleProprietorship':
      return 'SoleProprietorship';
    case 'TradingCompanyLimitedPartnerShip':
      return 'GeneralPartnership';
    case 'Association':
      return 'HousingCooperativeAssociation';
    case 'ForeignCorporation':
      return 'ForeignBranchWithinEES';
    case 'EstateOfDeceased':
      return 'SoleProprietorship';
    case 'Foundation':
      return 'NonProfitAssociation';
    case 'Authority':
      return 'Authority';
    case 'Other':
      return 'Other';
  }
}
